import type {
  GridColDef,
  GridFilterModel,
} from '@mui/x-data-grid-premium'

import LeadTableDateFilter, { type LeadTableDateFilterValue } from './LeadTableDateFilter'
import { buildInitialFilterModel, type DateFilterField } from '../leadTableQuery'
import type { LeadRow } from '../table/leadTableTypes'
import { renderHeaderWithFilter } from '../table/leadTableCellHelpers'
import { replaceFilterItem } from './leadTableFilterModel'

type BuildLeadTableColumnsWithFiltersParams = {
  columns: GridColDef<LeadRow>[]
  timezone: string
  filterModel: GridFilterModel
  onFilterModelChange: (model: GridFilterModel) => void
}

function buildDateFilterHeader(params: {
  field: DateFilterField
  title: string
  filterModel: GridFilterModel
  timezone: string
  onFilterModelChange: (model: GridFilterModel) => void
  onClear?: () => void
}) {
  const {
    field,
    title,
    filterModel,
    timezone,
    onFilterModelChange,
    onClear,
  } = params

  const filterItem = filterModel.items.find((item) => item.field === field)

  const filterValue: LeadTableDateFilterValue | undefined =
    filterItem?.operator && filterItem.value
      ? {
          operator: filterItem.operator,
          value: filterItem.value,
        } as LeadTableDateFilterValue
      : undefined

  const isActiveOverride =
    field === 'createdAt'
      ? filterValue?.value.label !== 'Today'
      : undefined

  return renderHeaderWithFilter({
    filterNode: (
      <LeadTableDateFilter
        title={title}
        timezone={timezone}
        value={filterValue}
        isActiveOverride={isActiveOverride}
        onClear={onClear}
        onChange={(value) => {
          onFilterModelChange(
            replaceFilterItem(
              filterModel,
              field,
              value
                ? {
                    id: field,
                    field,
                    operator: value.operator,
                    value: value.value,
                  }
                : null,
            ),
          )
        }}
      />
    ),
  })
}

export function buildLeadTableColumnsWithFilters({
  columns,
  timezone,
  filterModel,
  onFilterModelChange,
}: BuildLeadTableColumnsWithFiltersParams): GridColDef<LeadRow>[] {
  return columns.map((column) => {
    switch (column.field) {
      case 'createdAt':
        return {
          ...column,
          renderHeader: () =>
            buildDateFilterHeader({
              field: 'createdAt',
              title: 'Date',
              filterModel,
              timezone,
              onFilterModelChange,
              onClear: () => {
                // Clearing createdAt resets it to the default ('Today') rather than removing it ('Lifetime').
                const defaultCreatedAtFilter = buildInitialFilterModel(timezone).items.find(
                  (item) => item.field === 'createdAt'
                )

                onFilterModelChange(
                  replaceFilterItem(filterModel, 'createdAt', defaultCreatedAtFilter ?? null)
                )
              },
            }),
        }

      case 'statusChangedAt':
        return {
          ...column,
          renderHeader: () =>
            buildDateFilterHeader({
              field: 'statusChangedAt',
              title: 'Status Changed',
              filterModel,
              timezone,
              onFilterModelChange,
            }),
        }

      default:
        return column
    }
  })
}
